import { AlertTriangle, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { ParamMismatchInfo } from '../types.js';

interface ParameterRowProps {
  id: string;
  name: string;
  destId?: string | null;
  destName?: string | null;
  mismatch?: ParamMismatchInfo | null;
}

/**
 * Single row in the Replace flow's "Review Changes" parameter diff.
 * Verbatim port of `wf-audit/.../ParameterRow.tsx` — same three-col grid
 * (source name → arrow → destination name or "new") plus an expandable
 * mismatch panel showing per-field source/dest values when the auto-
 * matcher flagged a config or option-set diff.
 */
export function ParameterRow({
  id,
  name,
  destId,
  destName,
  mismatch,
}: ParameterRowProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div className="grid grid-cols-[minmax(0,1fr)_24px_minmax(0,1fr)] items-center gap-2">
        <div className="min-w-0 break-words text-foreground/85">
          {name}&nbsp;<span className="inline-flex text-foreground/40">({id})</span>
        </div>
        <div className="flex justify-center">
          <ArrowRight className="h-4 w-4 text-foreground/40" />
        </div>
        <div className="min-w-0 break-words text-foreground/85">
          {destName ? (
            <>
              {destName}&nbsp;<span className="inline-flex text-foreground/40">({destId})</span>
            </>
          ) : (
            <span className="text-emerald-700 dark:text-emerald-300">new</span>
          )}
        </div>
      </div>
      {mismatch && (
        <div className="ml-1 mt-1">
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200"
            onClick={() => setExpanded((prev) => !prev)}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            <span>
              {mismatch.severity === 'hard' ? 'Config mismatch' : 'Options differ'}:{' '}
              {mismatch.summary}
            </span>
          </button>
          {expanded && (
            <div className="mt-1 rounded border border-amber-400/30 bg-amber-500/10 p-2 text-xs">
              <table className="w-full text-left text-amber-100">
                <thead>
                  <tr className="border-b border-amber-400/30">
                    <th className="pb-1 pr-3 font-medium">Field</th>
                    <th className="pb-1 pr-3 font-medium">Source</th>
                    <th className="pb-1 font-medium">Destination</th>
                  </tr>
                </thead>
                <tbody>
                  {mismatch.diffs.map((diff) => (
                    <tr key={diff.field} className="border-b border-amber-400/20 last:border-0">
                      <td className="py-1 pr-3 font-mono">{diff.field}</td>
                      <td className="py-1 pr-3">{String(diff.sourceValue ?? 'null')}</td>
                      <td className="py-1">{String(diff.destValue ?? 'null')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
